/**
 * Tiny DOM builder
 *
 * `h('p', { class: 'lede' }, 'text', child)` — attributes, then children. Text
 * is always appended as a text node and never parsed, so nothing a user typed or
 * a rule file contained can become markup. The CSP forbids inline script anyway;
 * this keeps the stronger property that no string in this codebase is ever
 * interpreted as HTML.
 *
 * © Manish Jagdish Thatte
 */

import { getSettings } from '../store.js';
import { currentLocale } from './i18n.js';
import { applyNumerals } from './format.js';

/** Apply the reader's numeral preference to a formatted string. */
export function nz(text) {
  return applyNumerals(String(text), currentLocale(), getSettings().numerals);
}

export function h(tag, attrs = {}, ...children) {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs ?? {})) {
    if (v == null || v === false) continue;
    if (k === 'class') node.className = v;
    else if (k === 'dataset') Object.assign(node.dataset, v);
    // CSSOM, not setAttribute('style'): a strict style-src would block the attribute.
    else if (k === 'style') node.style.cssText = v;
    else if (k.startsWith('on') && typeof v === 'function') node.addEventListener(k.slice(2).toLowerCase(), v);
    else if (v === true) node.setAttribute(k, '');
    else node.setAttribute(k, String(v));
  }
  append(node, children);
  return node;
}

function append(node, children) {
  for (const c of children.flat(Infinity)) {
    if (c == null || c === false) continue;
    node.append(c instanceof Node ? c : document.createTextNode(String(c)));
  }
}

/** A `<table class="data-table">` from a header row and body rows of cells. */
export function table(head, rows, { caption = null, cls = 'data-table' } = {}) {
  return h('table', { class: cls },
    caption ? h('caption', {}, caption) : null,
    h('thead', {}, h('tr', {}, head.map((c) => h('th', { scope: 'col' }, c)))),
    h('tbody', {}, rows.map((r) => h('tr', {}, r.map((c, i) => (
      i === 0 ? h('th', { scope: 'row' }, c) : h('td', {}, c)
    ))))));
}

export function notice(text, kind = 'info') {
  return h('div', { class: `notice notice--${kind}`, role: kind === 'warning' ? 'alert' : null }, text);
}

/** A confidence / score bar: a text-and-shape carrier, never colour alone. */
export function bar(fraction, label) {
  const pct = Math.round(Math.max(0, Math.min(1, fraction)) * 100);
  return h('span', { class: 'bar', role: 'img', 'aria-label': `${label}: ${pct}%` },
    h('span', { class: 'bar__fill', style: `width:${pct}%` }),
    h('span', { class: 'bar__text' }, `${pct}%`));
}
